import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Award, Shield, TrendingUp } from 'lucide-react';
export const Certification = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <section id="certification" className="py-20 bg-white" data-id="element-302">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-303">
        <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} transition={{
        duration: 0.8
      }} variants={itemVariants} className="text-center mb-16" data-id="element-304">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-305">
            Ukweli Certified™ Program
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-6" data-id="element-306"></div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-307">
            The world's first three-tier evidence seal for supplements and
            functional products—empowering informed decisions based on
            scientific merit.
          </p>
        </motion.div>
        <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={containerVariants} className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-308">
          <motion.div variants={itemVariants} className="relative bg-white border-2 border-gray-200 rounded-lg p-8 hover:border-blue-500 transition-colors duration-300" data-id="element-309">
            <div className="absolute top-0 right-0 mt-4 mr-4" data-id="element-310">
              <Shield className="h-12 w-12 text-blue-100" data-id="element-311" />
            </div>
            <div className="mb-4" data-id="element-312">
              <span className="inline-block py-1 px-3 rounded-full text-xs font-semibold bg-blue-100 text-blue-800" data-id="element-313">
                Tier 1
              </span>
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900" data-id="element-314">
              Basic Verification
            </h3>
            <ul className="space-y-2 text-gray-700" data-id="element-315">
              <li className="flex items-start" data-id="element-316">
                <span className="text-green-500 mr-2" data-id="element-317">✓</span>
                <span data-id="element-318">Ingredient authenticity verified</span>
              </li>
              <li className="flex items-start" data-id="element-319">
                <span className="text-green-500 mr-2" data-id="element-320">✓</span>
                <span data-id="element-321">Contaminant testing completed</span>
              </li>
              <li className="flex items-start" data-id="element-322">
                <span className="text-green-500 mr-2" data-id="element-323">✓</span>
                <span data-id="element-324">Manufacturing standards assessment</span>
              </li>
            </ul>
          </motion.div>
          <motion.div variants={itemVariants} className="relative bg-white border-2 border-gray-200 rounded-lg p-8 hover:border-blue-500 transition-colors duration-300" data-id="element-325">
            <div className="absolute top-0 right-0 mt-4 mr-4" data-id="element-326">
              <Award className="h-12 w-12 text-blue-200" data-id="element-327" />
            </div>
            <div className="mb-4" data-id="element-328">
              <span className="inline-block py-1 px-3 rounded-full text-xs font-semibold bg-blue-100 text-blue-800" data-id="element-329">
                Tier 2
              </span>
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900" data-id="element-330">
              Efficacy Evidence
            </h3>
            <ul className="space-y-2 text-gray-700" data-id="element-331">
              <li className="flex items-start" data-id="element-332">
                <span className="text-green-500 mr-2" data-id="element-333">✓</span>
                <span data-id="element-334">All Tier 1 verifications</span>
              </li>
              <li className="flex items-start" data-id="element-335">
                <span className="text-green-500 mr-2" data-id="element-336">✓</span>
                <span data-id="element-337">In-vitro efficacy testing</span>
              </li>
              <li className="flex items-start" data-id="element-338">
                <span className="text-green-500 mr-2" data-id="element-339">✓</span>
                <span data-id="element-340">Bioavailability assessment</span>
              </li>
              <li className="flex items-start" data-id="element-341">
                <span className="text-green-500 mr-2" data-id="element-342">✓</span>
                <span data-id="element-343">Mechanism validation</span>
              </li>
            </ul>
          </motion.div>
          <motion.div variants={itemVariants} className="relative bg-white border-2 border-gray-200 rounded-lg p-8 hover:border-blue-500 transition-colors duration-300" data-id="element-344">
            <div className="absolute top-0 right-0 mt-4 mr-4" data-id="element-345">
              <TrendingUp className="h-12 w-12 text-blue-300" data-id="element-346" />
            </div>
            <div className="mb-4" data-id="element-347">
              <span className="inline-block py-1 px-3 rounded-full text-xs font-semibold bg-blue-100 text-blue-800" data-id="element-348">
                Tier 3
              </span>
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900" data-id="element-349">
              Clinical Validation
            </h3>
            <ul className="space-y-2 text-gray-700" data-id="element-350">
              <li className="flex items-start" data-id="element-351">
                <span className="text-green-500 mr-2" data-id="element-352">✓</span>
                <span data-id="element-353">All Tier 1 & 2 verifications</span>
              </li>
              <li className="flex items-start" data-id="element-354">
                <span className="text-green-500 mr-2" data-id="element-355">✓</span>
                <span data-id="element-356">Human clinical trial data</span>
              </li>
              <li className="flex items-start" data-id="element-357">
                <span className="text-green-500 mr-2" data-id="element-358">✓</span>
                <span data-id="element-359">Long-term safety monitoring</span>
              </li>
              <li className="flex items-start" data-id="element-360">
                <span className="text-green-500 mr-2" data-id="element-361">✓</span>
                <span data-id="element-362">Peer-reviewed research publication</span>
              </li>
            </ul>
          </motion.div>
        </motion.div>
        <motion.div variants={itemVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} transition={{
        delay: 0.6
      }} className="mt-12 text-center" data-id="element-363">
          <a href="#contact" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-300" data-id="element-364">
            Apply for Certification
          </a>
        </motion.div>
      </div>
    </section>;
};